import React, { useState } from "react";
import { Button, ControlLabel, FormControl, FormGroup, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";

import { assert } from "../util/assert";
import { CenterContainer, PasswordData, validatePassword, PasswordForm } from "./Common";
import { initiatePasswordReset, completePasswordReset } from "./accountApi";
import "./Account.css";


const InitiatePasswordReset = () => {
    const [displaySuccess, setDisplaySuccess] = useState(false);
    const [email, setEmail] = useState<string>();
    const [resetEmailResent, setResetEmailResent] = useState(false);

    const submitInitiateReset = () => {
        assert(email, "Submit should be disabled without email");
        initiatePasswordReset(email)
            .then(() => setDisplaySuccess(true));
    };

    if (displaySuccess) {
        return (
            <CenterContainer>
                <div className="rg-shadow-box rg-message-content">
                    <h2>Verify your email address</h2>
                    <FontAwesomeIcon
                        icon={["fas", "envelope"]}
                        size="3x"
                        color="lightgray"
                        className="rg-email-icon"
                    />
                    <p>
                        We sent an email with password reset instructions to the submitted email address if it is associated with a QBank account.
                    </p>
                    <p>
                        It may take up to 10 minutes for the email to arrive. Check your spam folder if you can't find the email or click the link below to re-send.
                    </p>
                    <Button
                        bsStyle="link"
                        className="rg-resend"
                        onClick={ () => { setResetEmailResent(true); submitInitiateReset() } }
                    >
                        Resend password reset email
                    </Button>
                    { resetEmailResent && <div>Password reset email has been resent</div> }
                </div>
            </CenterContainer>
    );
    } else {
        return (
            <div>
                <div className="rg-open-text">
                    Reset your password
                </div>
                <CenterContainer>
                    <div className="rg-shadow-box">
                        <p className="rg-imperative-text">
                            Enter your email address below and we will send a link to set a new password.
                        </p>
                        <Form
                            onSubmit={ e => { e.preventDefault(); submitInitiateReset() } }
                        >
                            <FormGroup>
                                <ControlLabel>Email Address</ControlLabel>
                                <FormControl
                                    type="email"
                                    autoComplete="email"
                                    label="Email"
                                    onChange={e => setEmail((e.target as any).value)}
                                />
                            </FormGroup>
                            <div className="rg-register-buttons">
                                <Button
                                    className="rg-register-button"
                                    bsStyle="link"
                                    type="submit"
                                    disabled={!email}
                                >
                                    Send Email
                                </Button>
                            </div>
                        </Form>
                    </div>
                </CenterContainer>
            </div>
        );
    }
};

const CompletePasswordReset = (resetToken: string ) => {
    const [displayMessage, setDisplayMessage] = useState<"completed" | "error" | undefined>();
    const [passwordData, setPasswordData] = useState<PasswordData>();

    const submitCompleteReset = () => {
        assert(passwordData, "Submit button should be disabled");
        completePasswordReset(passwordData.password, resetToken)
            .then(() => setDisplayMessage("completed"))
            .catch(() => setDisplayMessage("error"));
    };

    if (displayMessage === "completed") {
        return (
            <CenterContainer>
                <div className="rg-shadow-box rg-message-content">
                    <h2>You're all ready to go</h2>
                    <FontAwesomeIcon
                        icon={["fas", "check-circle"]}
                        size="3x"
                        color="lightgray"
                        className="rg-email-icon"
                    />
                    <p>
                        Return to the <Link to="/">homepage</Link> to log in!
                    </p>
                </div>
            </CenterContainer>
        );
    } else if (displayMessage === "error") {
        return (
            <CenterContainer>
                <div className="rg-shadow-box rg-message-content">
                    <h2>Failed to reset password</h2>
                    <FontAwesomeIcon
                        icon={["fas", "times-circle"]}
                        size="3x"
                        color="lightgray"
                        className="rg-email-icon"
                    />
                    <p>
                        The password reset link is invalid. The link sent in the password reset email expires after one hour. Please visit the <Link to="/resetPassword">password reset page</Link> to try again.
                    </p>
                </div>
            </CenterContainer>
        );
    } else {
        return (
            <div>
                <div className="rg-open-text">
                    Almost ready to go. Set a new password to login into your QBank account.
                </div>
                <CenterContainer>
                    <div className="rg-shadow-box">
                        <Form
                            onSubmit={ e => { e.preventDefault(); submitCompleteReset() } }
                        >
                            <PasswordForm
                                passwordData={passwordData}
                                setPasswordData={setPasswordData}
                            />
                            <div className="rg-register-buttons">
                                <Button
                                    className="rg-register-button"
                                    bsStyle="link"
                                    disabled={!validatePassword(passwordData)}
                                    type="submit"
                                >
                                    Set Password
                                </Button>
                            </div>
                        </Form>
                    </div>
                </CenterContainer>
            </div>
        );
    }
};

export const ResetPassword = ({ resetToken }: { resetToken?: string }) => {
    if (resetToken) {
        return CompletePasswordReset(resetToken);
    } else {
        return InitiatePasswordReset();
    }
};
